import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAsyncResource, UnauthorizedError } from '../composables/useAsyncResource'
import { useChannelSubscription } from '../composables/useChannelSubscription'
import { apiMutate } from '../composables/apiMutate'
import { apiGet } from '../composables/apiGet'
import {
  applyReviewQueueEvent,
  spliceReviewQueueRow,
  type ReviewQueueChannelPayload,
} from '../composables/reviewQueueChannel'

// Single source for the /lint admin review queue. Holds the list of
// pending content-finding notes and a live ActionCable subscription
// that splices in :created / :updated events as the nightly Lint
// runs and the admin clicks through.
//
// Two related counts:
//   - `notes.length` is what the queue list shows (status-filtered).
//   - `openCount` is the badge value — pending row count from the most
//     recent server payload, which the channel also broadcasts so the
//     badge stays accurate even when the user is on a non-/lint route.
//
// The channel uses the uniform CRUD vocabulary (`:created` / `:updated`)
// and the SPA reads `record.status` to decide what to do — pending →
// splice in / replace; applied or dismissed → splice out.

export interface LintNotePatch {
  anchor: string
  before: string
  after: string
}

export interface LintNote {
  id: number
  kind: 'contradiction' | 'stale_claim' | 'unreinforced_claim'
  page_path: string
  description: string
  proposed_patch: LintNotePatch | null
  // `wont_fix` is a permanent dismissal: the admin marked the
  // finding intentional, so a later run won't re-open it.
  status: 'pending' | 'applied' | 'dismissed' | 'wont_fix'
  // How many runs have re-surfaced this still-pending finding.
  // 0 on first flag; the card shows "flagged N×" once it climbs.
  reflag_count: number
  // Finding concerns an external fact Lint couldn't check; no patch attached.
  unverified: boolean
  lint_run_id: number | null
  // Set when the note came from the write-time ContradictionCheck
  // rather than a nightly Lint run. Exactly one of lint_run_id /
  // ingestion_run_id is populated.
  ingestion_run_id: number | null
  applied_at: string | null
  dismissed_at: string | null
  applied_commit_sha: string | null
  created_at: string
  updated_at: string
  // The wiki page's current text, included on every list/show response
  // and every channel broadcast so the diff renderer never needs a
  // follow-up fetch. Optional only because backfilled rows whose page
  // has been deleted underneath them surface as the empty string.
  current_page_content: string
}

interface ListPayload {
  notes: LintNote[]
  open_count: number
}

// Channel payload shape is `Broadcastable`'s standard `{event, record,
// open_count}` envelope — see `composables/reviewQueueChannel.ts` for
// the shared splice rules this store and `useSynthesisSuggestionsStore`
// both run against.
type ChannelPayload = ReviewQueueChannelPayload<LintNote>

export const useLintNotesStore = defineStore('lintNotes', () => {
  const openCount = ref(0)

  // useAsyncResource owns the data ref + lifecycle flags. With
  // `initialValue: []` the overload narrows the ref type to
  // `Ref<LintNote[]>`. The fetcher also threads `open_count` into
  // the sidecar ref so the header badge stays accurate after every
  // list refresh.
  const {
    data: notes,
    loaded,
    error: loadError,
    needsAuth,
    refresh: fetchAll,
  } = useAsyncResource<LintNote[]>(
    async () => {
      const res = await fetch(`/api/v1/lint_notes?status=pending`, { credentials: 'include' })
      if (res.status === 401) throw new UnauthorizedError()
      if (!res.ok) throw new Error(`Failed to load lint notes: ${res.status}`)
      const body: ListPayload = await res.json()
      openCount.value = body.open_count
      return body.notes
    },
    { initialValue: [] },
  )

  function fetchOne(id: number): Promise<LintNote | null> {
    return apiGet<LintNote>(`/api/v1/lint_notes/${id}`, {
      failureLabel: `Failed to load lint note ${id}`,
      tolerateStatuses: [404],
    })
  }

  async function accept(id: number) {
    const result = await mutate(id, 'accept')
    spliceReviewQueueRow(notes, openCount, id)
    return result
  }

  async function dismiss(id: number) {
    const result = await mutate(id, 'dismiss')
    spliceReviewQueueRow(notes, openCount, id)
    return result
  }

  // Permanent dismissal: same optimistic splice as dismiss — the
  // row leaves the pending list on the 202 ack — but the server lands it
  // in `wont_fix` so later runs never re-open it.
  async function wontFix(id: number) {
    const result = await mutate(id, 'wont_fix')
    spliceReviewQueueRow(notes, openCount, id)
    return result
  }

  async function regenerate(id: number, prompt_hint = '') {
    return mutate(id, 'regenerate', { prompt_hint })
  }

  function mutate(id: number, action: string, body?: Record<string, unknown>) {
    return apiMutate(`/api/v1/lint_notes/${id}/${action}`, {
      method: 'POST',
      body,
      failureLabel: `${action} failed`,
    })
  }

  function applyChannelEvent(data: ChannelPayload) {
    applyReviewQueueEvent(data, notes, openCount)
  }

  function subscribe() {
    useChannelSubscription<ChannelPayload>('LintNoteChannel', {
      received: applyChannelEvent,
      // Re-fetch on every reconnect so any events emitted during a
      // transient WS drop don't leave the queue stale. The skip-the-
      // first-connect-to-avoid-a-double-fetch rule is owned by
      // useChannelSubscription.
      refetchOnReconnect: () => void fetchAll(),
    })
  }

  // `!needsAuth` is load-bearing: useAsyncResource sets loaded=true on a
  // 401 too, so without this guard a stale-session 401 (notes=[],
  // loadError=null) would read as the benign "every finding reviewed"
  // empty state instead of an auth failure.
  const isEmpty = computed(() => loaded.value && !needsAuth.value && notes.value.length === 0)

  return {
    notes,
    loaded,
    loadError,
    needsAuth,
    openCount,
    isEmpty,
    fetchAll,
    fetchOne,
    accept,
    dismiss,
    wontFix,
    regenerate,
    subscribe,
  }
})
